import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../store/ToastProvider";
import { baseAppState, useApp } from "../store/AppProvider";
import { baseUserState, useUser } from "../store/UserProvider";
import { useTenant } from "../store/TenantProvider";
import { usePost } from "./usePost";
import { MESSAGES } from "../messages";
import { TokenDecodedContext } from "../types/loginTypes";
import { ROUTES } from "../routes/routeNames";
import { LOCAL_STORAGE_TOKEN_KEY } from "../services/api/config";
import { UserRole, CognitoClaim } from "../models/userModels";

interface LoginParams {
  username: string;
  password: string;
}

interface LoginResponse {
  authenticated: boolean;
  token: string;
}

interface MagicLinkValidateParams {
  token: string;
}

interface MagicLinkValidateResponse {
  tokens: {
    id_token: string;
  };
}

interface RetrieveContextParams {
  token: string;
  is_refresh: boolean;
}

export type LoginType = "credentials" | "token" | "magic-link";

export const useLogin = (): UseLogin => {
  const [loginError, setLoginError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { app, setApp } = useApp();
  const { setUser } = useUser();
  const { setTenant } = useTenant();

  const {
    sendData: authenticate,
    error: authError,
    isLoading: isAuthenticating,
  } = usePost<LoginResponse, LoginParams>();

  const {
    sendData: validateMagicLink,
    error: magicLinkError,
    isLoading: isValidatingMagicLink,
  } = usePost<MagicLinkValidateResponse, MagicLinkValidateParams>();

  const {
    sendData: retrieveContext,
    error: contextError,
    isLoading: isRetrievingContext,
  } = usePost<TokenDecodedContext, RetrieveContextParams>();

  useEffect(() => {
    if (authError || contextError || magicLinkError) {
      setLoginError(
        authError?.error_string ||
          contextError?.error_string ||
          magicLinkError?.error_string ||
          MESSAGES.DEFAULT_LOGIN_ERROR_MESSAGE
      );
    }
  }, [authError, contextError, magicLinkError]);

  useEffect(() => {
    if (loginError) {
      addToast(loginError, "error");
    }
  }, [loginError]);

  /**
   * Sets the authenticated user state and navigates appropriately.
   */
  const setAuthenticatedState = useCallback(
    (contextData: TokenDecodedContext, token: string, loginType: LoginType) => {
      // Set providers / application state
      setUser({ ...contextData.user_context, isAuthenticated: true });
      setTenant(contextData.tenant_context);
      setApp({ ...app, authorisation: { token } });

      // Save token in localStorage for persistence
      localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, token);

      // Show success toast for magic link
      if (loginType === "magic-link") {
        addToast(MESSAGES.MAGIC_LINK.SUCCESS, "success");
      }

      // Navigate based on role
      const userRole = contextData.user_context[CognitoClaim.ROLE];
      if (userRole === UserRole.MANAGER_UPLOAD) {
        navigate(ROUTES.ADD_FILE);
      } else if (loginType !== "token") {
        // Don't navigate if re-authenticating with stored token
        navigate(ROUTES.CONTROL_PANEL);
      }
    },
    [addToast, app, navigate, setApp, setTenant, setUser]
  );

  /**
   * Logs in using username/password credentials.
   */
  const handleLogin = async (
    username: string | undefined,
    password: string | undefined,
    setError: (errMsg: string) => void,
    tokenParam?: string
  ): Promise<void> => {
    setError("");

    try {
      let tokenToUse = tokenParam;
      const loginType: LoginType = tokenParam ? "token" : "credentials";

      // If no tokenParam, authenticate with user/pass
      if (!tokenToUse && username && password) {
        const authData = await authenticate("AUTHENTICATE", {
          username,
          password,
        } as LoginParams);

        if (!authData?.authenticated) {
          setError("Invalid credentials");
          return;
        }
        tokenToUse = authData.token;
      }

      if (!tokenToUse) {
        setError("No valid token or credentials provided.");
        return;
      }

      // Retrieve user/tenant context with the token
      const contextData = (await retrieveContext("RETRIEVE_USER_CONTEXT", {
        token: tokenToUse,
        is_refresh: false,
      } as RetrieveContextParams)) as TokenDecodedContext;

      setAuthenticatedState(contextData, tokenToUse, loginType);
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      setError(errMsg);
      addToast(errMsg, "error");
    }
  };

  /**
   * Logs in using a magic link token.
   */
  const handleMagicLinkLogin = useCallback(
    async (magicToken: string): Promise<void> => {
      if (!magicToken) {
        const errorMsg = MESSAGES.MAGIC_LINK.ERROR.MISSING_TOKEN;
        setLoginError(errorMsg);
        addToast(errorMsg, "error");
        navigate(ROUTES.LOGIN);
        return;
      }

      try {
        // 1) Validate the magic link token
        const validateResponse = await validateMagicLink(
          "VALIDATE_MAGIC_LINK",
          { token: magicToken } as MagicLinkValidateParams
        );

        if (!validateResponse?.tokens?.id_token) {
          throw new Error(MESSAGES.MAGIC_LINK.ERROR.VALIDATION_FAILED);
        }

        const idToken = validateResponse.tokens.id_token;

        // 2) Retrieve user/tenant context with the id_token
        const contextData = (await retrieveContext("RETRIEVE_USER_CONTEXT", {
          token: idToken,
          is_refresh: false,
        } as RetrieveContextParams)) as TokenDecodedContext;

        if (!contextData?.user_context) {
          throw new Error(MESSAGES.MAGIC_LINK.ERROR.CONTEXT_RETRIEVAL_FAILED);
        }

        // 3) Set state and navigate
        setAuthenticatedState(contextData, idToken, "magic-link");
      } catch (err) {
        const errMsg =
          err instanceof Error
            ? err.message
            : MESSAGES.MAGIC_LINK.ERROR.GENERIC;
        setLoginError(errMsg);
        addToast(errMsg, "error");
        navigate(ROUTES.LOGIN);
      }
    },
    [
      addToast,
      navigate,
      retrieveContext,
      setAuthenticatedState,
      validateMagicLink,
    ]
  );

  const handleLogout = useCallback((): void => {
    localStorage.removeItem(LOCAL_STORAGE_TOKEN_KEY);
    setUser(baseUserState);
    setTenant(null);
    setApp(baseAppState);
    navigate(ROUTES.LOGIN);
  }, [navigate, setApp, setTenant, setUser]);

  return {
    handleLogin,
    handleMagicLinkLogin,
    handleLogout,
    isLoading: isAuthenticating || isRetrievingContext || isValidatingMagicLink,
    error: loginError,
  };
};

export type UseLogin = {
  handleLogin: (
    username: string,
    password: string,
    setError: (errMsg: string) => void,
    tokenParam?: string
  ) => Promise<void>;
  handleMagicLinkLogin: (magicToken: string) => Promise<void>;
  handleLogout: () => void;
  isLoading: boolean;
  error: string | null;
};
